import Vue from 'vue';
import Vuex from 'vuex';
import { defaultThemes } from './constants';
import { eFeatureFlagTypes, eThemes, eTranslationLocaleCode } from './enums';
import { User as FirebaseUser } from '@firebase/auth-types';
import { deferredServiceWorkerEvents } from './util-functions/initialization-utils';
import { changeLanguage, setDefaultLanguage } from './util-functions/language-utils';
import { getParameterByName, routerDupeErrorCatch } from './util-functions/misc-utils';
import router from './router';
import { Timestamp } from 'firebase/firestore';
import { processServiceWorkerEvent } from './util-functions/service-worker-utils';
import { AndroidShortcuts } from 'capacitor-android-shortcuts';
import { Capacitor } from '@capacitor/core';
import { setUpNotifications } from './util-functions/notification-utils';

Vue.use(Vuex);

export const globalEventBus = new Vue();
export const ping = new Audio('https://firebasestorage.googleapis.com/v0/b/sitch-app.appspot.com/o/manualUploads%2Fping.mp3?alt=media&token=67fc7db9-b2c7-4b47-9269-bbb509b0396b');
ping.volume = 0;
let notificationSoundLoop: number;
const entryPath = window.location.pathname + window.location.search;

// These empty object factories cannot be in constants.ts because of a circular reference in that case.
export const getEmptyUserModeGateway = (): PublicUserModeGateway => {
  return {
    docId: '',
    activeModeId: '',
    wrapperModeId: '',
    activeModeLinkId: '',
    wrapperModeLinkId: '',
    activeModeOwnerId: '',
    wrapperModeOwnerId: '',
    isSitchLinkActivated: false,
    hasPermanentPremium: false,
    loadNewActiveModesAutomatically: false,
    autoSitchTimer: 1000 * 60 * 10, // 10 mins by default.
    premiumSubscriptionId: '',
    stripeAccountsMap: null,
    themes: {},
    teamOwnerGatewayRef: null,
    assignedTeamThemeId: '',
  };
};

export const getEmptyUser = (): PlatformUser => {
  return {
    docId: '',
    displayName: '',
    email: '',
    modeOrder: [],
    modeShortCut1: '',
    modeShortCut2: '',
    folderOrder: [],
    folders: {},
    permalinks: {},
    managedAccounts: {},
    externalUseModeGroups: {},
    genericIdsBoundToThisAccount: {},
    storageQuota: 0,
    webPushTokens: [],
    nativePushTokens: [],
    additionalVerifiedEmails: [],
    wasCreatedBy: '',
    userProxyHandlerIds: [],
    joinedTeamsRefs: [],    
    isAffiliate: false,
    dateCreated: Timestamp.fromMillis(Date.now()),
    dateUpdated: Timestamp.fromMillis(Date.now()),
    settings: {
      locale: eTranslationLocaleCode.notSet,
      appTheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? eThemes.dark : eThemes.light,
      showNewPaymentAlert: false,
      showNewMessageAlert: false,
      showNewBookingAlert: false,
      showNewCustomFormAlert: false,
      shouldNotificationSoundRepeat: false,
      getEmailNotificationsForPayments: true,
      getEmailNotificationsForBookings: true,
      getEmailNotificationsForCustomForms: true,
      getEmailNotificationsForMarketing: true,
      getWebPushNotificationsForPayments: false,
      getWebPushNotificationsForBookings: false,
      getWebPushNotificationsForCustomForms: false,
      getNativePushNotificationsForPayments: false,
      getNativePushNotificationsForBookings: false,
      getNativePushNotificationsForCustomForms: false,
    },
  };
};

interface FeatureFlags {
  [key: string]: eFeatureFlagTypes;
}

export interface StripeSubscription {
  status: 'active' | '';
  customer: string;
  cancel_at: number;
}

export interface GlobalState {
  isLoggedIn: boolean;
  userId: string;
  proxiedUser: PlatformUser;
  currUser: PlatformUser;  
  currUserModeGateway: PublicUserModeGateway;
  modes: ModesMap;
  folders: FoldersMap;
  isPremiumActive: boolean;
  premiumSubData: StripeSubscription | null;
  premiumCancelDate: number;
  storageQuota: number;
  moneySymbol: string;
  stripeAccountsMap: { [stripeAccountId: string]: StripeAccount } | null;
  fonts: Font[];
  initialLoadIsHappening: boolean;
  isLoading: boolean;
  locale: eTranslationLocaleCode;
  appTheme: eThemes;
  currAlertObject: AlertObject | null;
  idsGeneratedSinceLastModeFormPopulation: string[];
  serviceWorkerRegistation: ServiceWorkerRegistration | null;
  firebaseUser: FirebaseUser | null;
  brandIconsAlreadyAdded: boolean;
  recentStripeInvoices: any[];
  currentFileUploads: FileUploadsObject;
  joinedTeamsObjects: { [teamModeId: string]: Team };
  createdTeams: { [teamId: string]: Team };
  featureFlags: FeatureFlags;
  isAdmin: boolean;
  dataForEmailSignUp: {
    displayName: string;
  };
}

// For updating a prop in a map in the store.
export const storeMapPropUpdate = (stateProp: string, id: string, update: any) => {
  const statePropMap: { [id: string]: any } = (store.state as any)[stateProp];
  store.commit(stateProp, {
    ...statePropMap,
    [id]: {
      ...statePropMap[id],
      ...update,
    },
  });
};

function initialState() {
  const state: GlobalState = {
    stripeAccountsMap: null,
    isLoggedIn: false,
    userId: '',
    proxiedUser: getEmptyUser(),
    currUser: getEmptyUser(),
    currUserModeGateway: getEmptyUserModeGateway(),
    modes: {},
    folders: {},
    isPremiumActive: false,
    premiumSubData: null,
    premiumCancelDate: 0,
    moneySymbol: '$',
    storageQuota: 0,
    fonts: [],
    initialLoadIsHappening: true,
    isLoading: false,
    locale: eTranslationLocaleCode.en,
    appTheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? eThemes.dark : eThemes.light,
    serviceWorkerRegistation: null,
    currAlertObject: null,
    idsGeneratedSinceLastModeFormPopulation: [],
    firebaseUser: null,
    brandIconsAlreadyAdded: false,
    recentStripeInvoices: [],
    currentFileUploads: {},
    joinedTeamsObjects: {},
    createdTeams: {},
    isAdmin: false,
    dataForEmailSignUp: {
      displayName: '',
    },
    featureFlags: {
      profileModes: eFeatureFlagTypes.prod,
      urlRedirectModes: eFeatureFlagTypes.prod,
      articleModes: eFeatureFlagTypes.prod,
      fileModes: eFeatureFlagTypes.prod,
      siteModes: eFeatureFlagTypes.prod,
      linkListModes: eFeatureFlagTypes.prod,
      groupModes: eFeatureFlagTypes.prod,
      wifiModes: eFeatureFlagTypes.prod,
      galleryModes: eFeatureFlagTypes.prod,
      shopModes: eFeatureFlagTypes.prod,
      htmlModes: eFeatureFlagTypes.prod,
      sitchCam: eFeatureFlagTypes.prod,
      eventsModes: eFeatureFlagTypes.prod,
      bookingModes: eFeatureFlagTypes.prod,
      customFormModes: eFeatureFlagTypes.prod,
      personalPaymentModes: eFeatureFlagTypes.prod,
      businessPaymentModes: eFeatureFlagTypes.prod,
      blogModes: eFeatureFlagTypes.prod,
      accordionModes: eFeatureFlagTypes.prod,
      returnToModes: eFeatureFlagTypes.prod,
      chatModes: eFeatureFlagTypes.prod,
      chessModes: eFeatureFlagTypes.prod,
      wordleModes: eFeatureFlagTypes.prod,
      triviaModes: eFeatureFlagTypes.prod,
      cardModes: eFeatureFlagTypes.prod,
      sitchPremium: eFeatureFlagTypes.prod,
      sitchDevices: eFeatureFlagTypes.prod,
      teams: eFeatureFlagTypes.prod,
      userCreation: eFeatureFlagTypes.prod,
      deviceImports: eFeatureFlagTypes.prod,
      fileUploadsInCustomForms: eFeatureFlagTypes.prod,
      modeGroups: eFeatureFlagTypes.prod,
    },
  };
  return state;
}

let cachedShortcutData = '';

function doShortcut() {
  switch (cachedShortcutData) {
    case 'sitchCam':
      router.push('SitchCam');
      return;
    case 'qrCode':
      router.push('QR');
      return;
    case 'quickSitch1':
      router.push('/QR/?msc=1');
      return;
    case 'quickSitch2':
      router.push('/QR/?msc=2');
      return;
  }
}

if (Capacitor.getPlatform() === 'android') {
  AndroidShortcuts.isDynamicSupported().then(({ result }) => {
    if (result) {
      const qrImage =
        'iVBORw0KGgoAAAANSUhEUgAAAMAAAADACAMAAABlApw1AAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAABaUExURUdwTAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP///8DAwICAgEBAQODg4GBgYCAgIDAwMKCgoPDw8BAQENDQ0JCQkL0szAkAAAAQdFJOUwCfH9/vP8+/D38vX0+Pb69joyUIAAAE4klEQVR42u2d2ZrcKAyFwew2LmuqK8ts7/+ac9OZifurqkYymMOkzmXSdPQHAQKMpFQLOZfTBy3ORQWv6FIKgR7KhzXlGdT4nIKlMoV1weqNKWtPTJltAemKOW0klE+5v/WeDsmsHRnictD6d4Y0dRq1hmqpw5iOqZ75RGT1ub0QV0u1pecTzacm2uaTnMdSK20nONLFUEutjYez89RY5jKg83+IlJr5UbZ0jtr4UdzoNJkG89Fs6ExVHwmJTlao6kYx0OmyrqL7WOqham609LGfSNdxo5W6yVdYEqKmjrLziMN3R3Bwxxk99dZy6LzEUH+lwe0/QADgP4e8CMZ+IQGQ/SICKPslBBuU/fzQThOYmGvyQnDynMguE6A8fPz/aXRdPAEZwlTpDicQqsqmogus/WRKBvJMwNoGHgClw2AjbM0jrgCM1SBadIBPtjcrvP1knx21OBpA4QnA53uA21upvu3afS1u9zlBPrKEvf1Wqtuu3bW43YHlrGQEIwA8HMcldwAQAPZ+F0w0CsCDwFqPA0CTtANQALR4Gw8CcKcLyjoABkBLgwgUAIrCKA4GIAn3kTAA9gOAGQ3gw2lpcRiKAxBkR6F7gOsujvxSDPD7rp0MYDeTlm/E9v/a2xMjb8V/JwRIorNcIAAjOooAAvjpgCLSkACr5DYACcBIDrOQAP7zITsowEVwmAIFEASnWVAAP0LSMCzA+4UHDQuQ+OeJWACB/0koFgDxrzT2AF+uP+uPYoC/du0OAMzMVQBpP/DvSjDRwACafScABhDY18JgAJZ9qwQGQJH7YQEagCs/UMEEWHiBBB5AYs6icACae7OKBhDGB0hjA9AvB/DtVqo/d+2+F7fjAsB94coFCC+AF8AL4AXwAngBvAA6AgwfSqRfDODrtVTfd+3+vvcj+1999w+x9wP7Xy3cD+SxAXzXPXEFgDA+gBobIP0PADoe7l7v3TTxABz31Q/ascrMzVuDBsB+twQG4Nkv98AANu6XBmgAif14FQzAsd+O1fnUoBpAZL+fxwIwnJcDiACa9+E3HsDCzyGBBTDxk0hAARje8xk8AF3+BrQywG0XQIsBsiCPARKAlSQyQALY+C9osAAWyUt0JIAoSYYBBLDx3qLjAWRRPpU64TT3Ov75HMR6xoQDkGQ5tXAAJtFrXBwALcyKBAPghEk9UACCNDEVCoCTJuapCrCf+TnrgJclVsEByOLUNhgAQZ6eDQPAybMLQQCEAykuIQAe5niKYwCsQEn6RWHos3R/ZgCAp9lrB8gSFtTYedrsNHSqxYJki25oB4J3IltSUcEDAxRVI5hxE0YWpg1eUO0vTj29DjwAkPMeM9KvR8SBzEqADziQtWIpoxEExdQy6AQESuAlVZnWISdQyCoQ4opMIH1woEIfxB7ZHKnplQYdv0DFCPzRqnBL3xVtU4flehKsNWrydSzqtagq6lXWrmJlyi4Lgq9ZJbdDcKpVVU0nb3GOVkPs7Ua+RYXiE90oqSY6q0hoaFfufT5hJNiWVcaVSq39SDeu866mpn4UnGqvudnCbLI6R64JglnUeaqPcKr5Sik1Vx0LIavzFVOlbvB6Up0UL4f3CnbtZv37aDjkSmFRAJq1aIE2W1YwmjIPwoRlVmiacwolgUZYL07BKrqcwoPZyYf14mr/x/8DP4I22735gKMAAAAASUVORK5CYII=';
      const cameraImage =
        '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';
      AndroidShortcuts.setDynamic({
        items: [
          {
            id: 'sitchCam',
            shortLabel: 'Sitch Cam',
            longLabel: 'Sitch Cam',
            icon: {
              type: 'Bitmap',
              name: cameraImage,
            },
            data: 'sitchCam',
          },
          {
            id: 'qrCode',
            shortLabel: 'QR Code',
            longLabel: 'QR Code',
            icon: {
              type: 'Bitmap',
              name: qrImage,
            },
            data: 'qrCode',
          },
          {
            id: 'quickSitch1',
            shortLabel: 'Quick Sitch 1',
            longLabel: 'Quick Sitch 1',
            data: 'quickSitch1',
          },
          {
            id: 'quickSitch2',
            shortLabel: 'Quick Sitch 2',
            longLabel: 'Quick Sitch 2',
            data: 'quickSitch2',
          },
        ],
      });
    }
  });

  // Triggered when app is launched by a shortcut
  AndroidShortcuts.addListener('shortcut', (response: any) => {
    cachedShortcutData = response.data;
    if (!store.state.initialLoadIsHappening) {
      doShortcut();
    }
  });
}
function onLoginStateChange(state: GlobalState, loggedInState: boolean) {
  setTimeout(() => {
    state.initialLoadIsHappening = false;
  }, 100); // Anti FOUC delay.
  function getQueryParams() {
    // ui_sd, ui_sid, oobCode, mode, lang, apiKey are all Firebase Auth params.
    const allQueryParamVars = ['uid', 'cft', 'iv', 'g', 'folderId', 'msc', 'type', 'code', 'error', 'error_description', 'ui_sid', 'ui_sd', 'oobCode', 'mode', 'lang', 'apiKey'];
    const ret: any = {};
    allQueryParamVars.forEach((paramString) => {
      const val = getParameterByName(paramString);
      if (val) {
        ret[paramString] = val;
      }
    });
    return ret;
  }
  Vue.nextTick(() => {
    if (loggedInState === true) {
      // Logging in in this case so go to the original path.
      if (!cachedShortcutData) {
        if (!entryPath.includes('/Login')) {
          router.push(entryPath).catch(routerDupeErrorCatch);
        } else {
          router.push({ path: '/', query: getQueryParams() }).catch(routerDupeErrorCatch);
        }
      } else {
        doShortcut();
      }
      // Not logged in so just go to login page.
    } else {
      router.push({ path: '/Login', query: getQueryParams() }).catch(routerDupeErrorCatch);
    }
  });
}
export const store = new Vuex.Store({
  state: initialState(),
  mutations: {
    reset(state) {
      // acquire initial state
      const s: GlobalState = initialState();
      Object.keys(s).forEach((key: string) => {
        (state as any)[key] = (s as any)[key];
      });
      onLoginStateChange(state, false);
    },
    joinedTeamsObjects(state, payload: { [teamId: string]: Team }) {
      state.joinedTeamsObjects = payload;
    },
    createdTeams(state, payload: { [teamId: string]: Team }) {
      state.createdTeams = payload;
    },
    currentFileUploads(state, payload: FileUploadsObject) {
      state.currentFileUploads = payload;
    },
    serviceWorkerRegistation(state, payload: ServiceWorkerRegistration) {
      state.serviceWorkerRegistation = payload;
      Vue.nextTick(() => {
        setUpNotifications();
      });
    },
    brandIconsAlreadyAdded(state, payload: boolean) {
      state.brandIconsAlreadyAdded = payload;
    },
    idsGeneratedSinceLastModeFormPopulation(state, payload: string[]) {
      state.idsGeneratedSinceLastModeFormPopulation = payload;
    },
    currAlertObject(state, payload: AlertObject | null) {
      clearInterval(notificationSoundLoop);
      state.currAlertObject = payload;
      if (payload) {
        ping.play();
        if (state.currUser.settings.shouldNotificationSoundRepeat) {
          notificationSoundLoop = window.setInterval(() => {
            ping.play();
          }, 7000);
        }
      }
    },
    locale(state, payload: eTranslationLocaleCode) {
      state.locale = payload;
      changeLanguage();
    },
    isLoggedIn(state, payload: boolean) {
      state.isLoggedIn = payload;
      onLoginStateChange(state, payload);
    },
    isLoading(state, payload: boolean) {
      state.isLoading = payload;
    },
    userId(state, payload: string) {
      state.userId = payload;
    },
    currUser(state, payload: PlatformUser) {
      const newUserData = {
        ...getEmptyUser(),
        ...payload,
        settings: {
          ...getEmptyUser().settings,
          ...payload.settings,
        },
      };
      state.currUser = newUserData;
      store.commit('storageQuota', payload.storageQuota || 0);
      store.commit('folders', payload.folders || {});
      const locale = state.currUser.settings?.locale;
      if (locale && locale !== 'notSet') {
        store.commit('locale', state.currUser.settings?.locale);
      } else {
        setDefaultLanguage();
      }
      state.appTheme = state.currUser.settings?.appTheme;
    },
    firebaseUser(state, payload: FirebaseUser) {
      state.firebaseUser = payload;
    },
    storageQuota(state, payload: number) {
      state.storageQuota = payload;
    },
    currUserModeGateway(state, payload: PublicUserModeGateway) {
      state.currUserModeGateway = {
        ...getEmptyUserModeGateway(),
        ...payload,
      };
      if (payload.stripeAccountsMap && Object.values(payload.stripeAccountsMap).length) {
        state.stripeAccountsMap = payload.stripeAccountsMap;
      }
    },
    modes(state, payload: ModesMap) {
      state.modes = payload;
    },
    folders(state, payload: FoldersMap) {
      state.folders = payload;
    },
    premiumSubData(state, payload: StripeSubscription | null) {
      state.premiumSubData = payload;
      if (payload) {
        state.isPremiumActive = payload.status === 'active';
        state.premiumCancelDate = payload.cancel_at;
      } else {
        state.isPremiumActive = false;
      }
      // We need to process these after premium check in case we need to make a new Sitch.
      if (deferredServiceWorkerEvents.length) {
        deferredServiceWorkerEvents.forEach((event: any) => {
          processServiceWorkerEvent(event);
        });
      }
    },
    recentStripeInvoices(state, payload: any[]) {
      state.recentStripeInvoices = payload;
    },
    fonts(state, payload: Font[]) {
      state.fonts = payload;
    },
    isAdmin(state, payload: boolean) {
      state.isAdmin = payload;
    },
    dataForEmailSignUp(state, payload: any) {
      state.dataForEmailSignUp = payload;
    },
  },
  getters: {
    themes(state): AestheticTheme[] {
      return [...Object.values(state.currUserModeGateway.themes), ...Object.values(defaultThemes)];
    },
    currWrapperMode(state): Mode | null {
      const currUserModeGateway: PublicUserModeGateway = state.currUserModeGateway;
      if (currUserModeGateway.wrapperModeOwnerId) {
        const activeModeId = currUserModeGateway.activeModeId;
        const allTeamModes: { [teamModeDocId: string]: TeamMode } = {};
        Object.values(state.joinedTeamsObjects).forEach((team) => {
          team.modes.forEach((mode) => {
            allTeamModes[mode.docId] = mode;
          });
        });
        const currTeamMode = allTeamModes[activeModeId];
        if (currTeamMode) {
          const teamMode: Mode = {
            docId: currTeamMode.docId,
            displayName: '',
            index: -1,
            themeId: '',
            linkId: currTeamMode.linkId,
            type: currTeamMode.type,
            name: currTeamMode.name,
          };
          return teamMode as Mode;
        }
      }
      if (currUserModeGateway && Object.keys(state.modes).length) {
        const currMode = state.modes[currUserModeGateway.wrapperModeId];
        return currMode || null;
      }
      return null;
    },
    currMode(state): Mode | null {
      const currUserModeGateway: PublicUserModeGateway = state.currUserModeGateway;
      if (currUserModeGateway.activeModeOwnerId) {
        const activeModeId = currUserModeGateway.activeModeId;
        const allTeamModes: { [teamModeDocId: string]: TeamMode } = {};
        Object.values(state.joinedTeamsObjects).forEach((team) => {
          team.modes.forEach((mode) => {
            allTeamModes[mode.docId] = mode;
          });
        });
        const currTeamMode = allTeamModes[activeModeId];
        if (currTeamMode) {
          const teamMode: Mode = {
            docId: currTeamMode.docId,
            displayName: '',
            index: -1,
            themeId: '',
            linkId: currTeamMode.linkId,
            type: currTeamMode.type,
            name: currTeamMode.name,
          };
          return teamMode as Mode;
        }
      }
      if (currUserModeGateway && Object.keys(state.modes).length) {
        const currMode = state.modes[currUserModeGateway.activeModeId];
        return currMode || null;
      }
      return null;
    },
    stripeAccounts(state): StripeAccount[] {
      return state.currUserModeGateway.stripeAccountsMap ? Object.values(state.currUserModeGateway.stripeAccountsMap) : [];
    },
    // TODO: Fix ordering glitch when you share a link using Sitch.
    modeOrder(state): string[] {
      const modeOrder: string[] = state.currUser.modeOrder ? [...state.currUser.modeOrder] : [];
      const allModeIds = Object.keys(state.modes);
      const modeIdsNotInMainOrder = allModeIds.filter((x) => !modeOrder.includes(x));

      // Check to see if mode ids in modeIdsNotInMainOrder exists in any folders. If so pluck them out.
      const modeIdsNotInMainOrderOrInFolderOrderArrays = modeIdsNotInMainOrder.filter((modeId) => {
        const existInFolder = Object.values(state.currUser.folders || []).some((folder) => folder.modeOrder.includes(modeId));
        return !existInFolder; // If false the current mode does not exist in the main order or in any folder.
      });

      const fullResult = modeOrder.concat(modeIdsNotInMainOrderOrInFolderOrderArrays);

      return fullResult;
    },
    teamModesArray(state): ProccessedTeamMode[] {
      const teams = Object.values(state.joinedTeamsObjects);
      return teams.flatMap((team) => {
        return team.modes.map((mode) => {
          return {
            ...mode,
            ownerId: team.teamOwnerId,
          };
        });
      });
    },
    teamModesMap(): { [modeId: string]: ProccessedTeamMode } {
      const teamModes = store.getters.teamModesArray as ProccessedTeamMode[];
      const ret: { [modeId: string]: ProccessedTeamMode } = {};
      teamModes.forEach((mode) => {
        ret[mode.docId] = mode;
      });
      return ret;
    },
    teamAndPersonalModesArray(state): Mode | ProccessedTeamMode[] {
      const teamModes = store.getters.teamModesArray as ProccessedTeamMode[];
      const modes = Object.values(state.modes).sort((a: Mode, b: Mode) => {
        return a.index - b.index;
      }) as Mode[];
      return [...modes, ...teamModes] as Mode | ProccessedTeamMode[];
    },
    teamAndPersonalModesMap(state): { [modeId: string]: ProccessedTeamMode | Mode } {
      return {
        ...state.modes,
        ...(store.getters.teamModesMap as { [modeId: string]: ProccessedTeamMode }),
      };
    },
    sortedModes(state): Mode[] {
      const modeOrder: string[] = [...store.getters.modeOrder];
      const modesArray: AnyMode[] = Object.values(state.modes);

      const ret: AnyMode[] = [];
      if (modesArray.length) {
        modeOrder.forEach((modeId: string) => {
          const mode = state.modes[modeId];
          if (mode) {
            ret.push({ ...mode });
          }
        });
      }
      return ret;
    },
    sortedFolders(state): Folder[] {
      const folderOrder: string[] = state.currUser.folderOrder ? [...state.currUser.folderOrder] : [];
      const foldersArray: Folder[] = Object.values(state.folders);

      const ret: Folder[] = [];
      if (foldersArray.length) {
        folderOrder.forEach((folderId: string) => {
          const folder = state.folders[folderId];
          if (folder) {
            ret.push({ ...folder });
          }
        });
      }
      return ret;
    },
  },
});
